/* ==========================================================================
   GemofU cart UX
   Loaded site-wide from layout/theme.liquid.

   1. Header cart counter. Forge's header renders the cart as an icon only and
      emits no count at all in that mode. The badge is injected here, next to
      the wishlist heart and styled to match it, so no Forge header file has to
      be edited. It carries Forge's own .js-cart-count class, so cart.js keeps
      it up to date on every add, quantity change and removal; the starting
      value comes from one /cart.js read because Forge never initialises it.

   2. Quick add. There are genuinely two different actions behind that +, and
      the reason some of them looked broken is that they were drawn
      identically:

        - A product with one variant (most necklaces) can go straight into the
          bag. One POST, and the tick is now shown optimistically so it lands
          on the same frame as the press rather than after the round trip.

        - A product with sizes or colours (every bangle: 2.4 / 2.6 / 2.8 /
          2.10, and often a colour too) CANNOT be added without the shopper
          choosing. Those have to open the picker, which means fetching it.
          They are now marked with .gou-needs-options so they read as a
          different control, they show a spinner on press, and the picker HTML
          is prefetched on first hover or touch so the drawer opens instantly.

   3. Re-arms the quick-add buttons after the product grid is replaced by AJAX
      (filtering, sorting, pagination). Forge binds its click handlers once on
      DOMContentLoaded, so any card that arrived later had a dead button.
   ========================================================================== */
(function () {
  'use strict';

  /* ---- 1. Header cart counters ----------------------------------------- */
  // The badge injected below AND Forge's own mobile count. Both get a
  // data-empty flag so the badge hides itself when the bag is empty.
  // (gou-fixes.css section 10 draws the mobile count as a small pill on a bag
  // icon, so hiding it no longer leaves a blank gold disc behind.)
  var COUNTERS = '.gou-cart-count, .section-header__mobile .js-cart-count';

  function syncBadges() {
    document.querySelectorAll(COUNTERS).forEach(function (el) {
      var n = parseInt((el.textContent || '').replace(/[^0-9]/g, ''), 10);
      el.setAttribute('data-empty', (!n || n < 1) ? 'true' : 'false');
    });
  }

  function injectBadges() {
    var added = false;

    document.querySelectorAll('.header__cart-links--cart').forEach(function (li) {
      if (li.querySelector('.gou-cart-count')) return;

      var badge = document.createElement('span');
      badge.className = 'js-cart-count gou-cart-count';
      badge.setAttribute('data-empty', 'true');
      badge.setAttribute('aria-hidden', 'true');
      badge.textContent = '';

      li.appendChild(badge);
      added = true;
    });

    if (added) syncBadges();
    return added;
  }

  function watchBadge(el) {
    if (el.hasAttribute('data-gou-watched')) return;
    el.setAttribute('data-gou-watched', '');
    new MutationObserver(syncBadges).observe(el, {
      childList: true,
      characterData: true,
      subtree: true
    });
  }

  function watchBadges() {
    document.querySelectorAll(COUNTERS).forEach(watchBadge);
  }

  function loadInitialCount() {
    fetch('/cart.js', { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        document.querySelectorAll('.gou-cart-count').forEach(function (el) {
          el.textContent = cart.item_count > 0 ? String(cart.item_count) : '';
        });
        syncBadges();
      })
      .catch(function () {});
  }

  /* ---- 2. Quick add ----------------------------------------------------- */
  var toastTimer = null;

  function toast(message, isError) {
    var el = document.querySelector('.gou-toast');

    if (!el) {
      el = document.createElement('div');
      el.className = 'gou-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.innerHTML =
        '<span class="gou-toast__tick" aria-hidden="true">&#10003;</span>' +
        '<span class="gou-toast__msg"></span>';
      document.body.appendChild(el);
    }

    el.querySelector('.gou-toast__tick').innerHTML = isError ? '&#33;' : '&#10003;';
    el.querySelector('.gou-toast__msg').textContent = message;

    // Force a reflow so the transition replays on a repeat add.
    void el.offsetWidth;
    el.classList.add('gou-toast--in');

    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      el.classList.remove('gou-toast--in');
    }, 2800);
  }

  function cartConfig() {
    var node = document.getElementById('cart-config');
    if (!node) return null;
    try { return JSON.parse(node.innerHTML || '{}'); } catch (e) { return null; }
  }

  // Several adds in a row would otherwise each rebuild the drawer, so the
  // refresh is collapsed into one once the queue goes quiet.
  var refreshTimer = null;

  function refreshCart() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      if (!window.WAU || !WAU.ThemeCart || !WAU.AjaxCart) return;

      var config = cartConfig();
      if (!config) return;

      WAU.ThemeCart.getCart().then(function (cart) {
        WAU.AjaxCart.updateView(config, cart);
      }).catch(function () {});
    }, 250);
  }

  function showAdded(button) {
    button.classList.remove('gou-busy', 'gou-pressed', 'loading-quickshop');
    button.classList.add('gou-added', 'gou-pop');

    clearTimeout(button._gouPop);
    clearTimeout(button._gouClear);
    button._gouPop = setTimeout(function () { button.classList.remove('gou-pop'); }, 520);
    button._gouClear = setTimeout(function () { button.classList.remove('gou-added'); }, 2400);
  }

  function clearAdded(button) {
    clearTimeout(button._gouPop);
    clearTimeout(button._gouClear);
    button.classList.remove('gou-added', 'gou-pop', 'gou-busy', 'gou-pressed');
  }

  // Shopify's cart API does not handle overlapping writes: firing several
  // /cart/add.js posts at once silently drops most of them (11 rapid clicks
  // landed 3 items in testing). Adds are queued so only one is ever in
  // flight; the tick is already on screen, so queuing is invisible.
  var queue = Promise.resolve();

  function postAdd(button, id, title) {
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: id, quantity: 1 })
    })
      .then(function (response) {
        if (!response.ok) return response.json().then(function (body) { throw body; });
        return response.json();
      })
      .then(function () {
        toast(title ? title + ' added to bag' : 'Added to bag');
        refreshCart();
      })
      .catch(function (err) {
        // The tick was shown optimistically, so take it back honestly.
        clearAdded(button);
        toast((err && err.description) ? err.description : 'Sorry, that could not be added', true);
      });
  }

  // A product with a single variant carries its variant id on the button, so
  // the add is one small POST. The confirmation is shown immediately rather
  // than after the round trip: the request nearly always succeeds, and on the
  // rare failure the tick is removed and the toast explains. That removes the
  // pause between pressing + and seeing anything happen.
  function directAdd(button) {
    var id = parseInt(button.getAttribute('data-variant-id'), 10);
    if (!id) return false;

    var card = button.closest('.product-loop__item');
    var title = card ? (card.getAttribute('data-alpha') || '') : '';

    showAdded(button);

    queue = queue.then(function () {
      return postAdd(button, id, title);
    });

    return true;
  }

  /* ---- Products that need options --------------------------------------- */
  // Marked so they are visibly a different control, and prefetched so the
  // picker drawer opens without a wait.
  function markOptionButtons() {
    document.querySelectorAll('.product-listing__quickview-trigger').forEach(function (button) {
      if (button.hasAttribute('data-gou-kind')) return;

      var direct = button.hasAttribute('data-quick-add') && button.hasAttribute('data-variant-id');
      button.setAttribute('data-gou-kind', direct ? 'add' : 'options');

      if (!direct) {
        button.classList.add('gou-needs-options');
        var single = button.hasAttribute('data-gou-single');
        button.setAttribute('aria-label', single ? 'Quick view' : 'Choose options');
        button.setAttribute('title', single ? 'Quick view' : 'Choose a size');
      }
    });
  }

  // Forge fetches the picker markup only once the button is clicked. Warming
  // it on hover or first touch means the drawer is usually ready before the
  // finger lifts.
  var prefetched = {};

  function prefetchPicker(button) {
    var url = button.getAttribute('data-product-url');
    if (!url || prefetched[url]) return;
    prefetched[url] = true;
    fetch(url, { credentials: 'same-origin' }).catch(function () {});
  }

  function bindPrefetch() {
    ['pointerenter', 'touchstart'].forEach(function (evt) {
      document.addEventListener(evt, function (event) {
        var button = event.target.closest && event.target.closest('.product-listing__quickview-trigger');
        if (button && button.getAttribute('data-gou-kind') === 'options') prefetchPicker(button);
      }, { capture: true, passive: true });
    });

    // Also warm whatever is on screen, cheaply, once things are idle.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          if (entry.target.getAttribute('data-gou-kind') === 'options') {
            var target = entry.target;
            if (window.requestIdleCallback) {
              window.requestIdleCallback(function () { prefetchPicker(target); }, { timeout: 2000 });
            } else {
              setTimeout(function () { prefetchPicker(target); }, 600);
            }
          }
        });
      }, { rootMargin: '200px' });

      var observe = function () {
        document.querySelectorAll('.product-listing__quickview-trigger[data-gou-kind="options"]:not([data-gou-io])').forEach(function (b) {
          b.setAttribute('data-gou-io', '');
          io.observe(b);
        });
      };
      observe();
      window.__gouObservePickers = observe;
    }
  }

  function bindQuickAdd() {
    // Press feedback first, on pointerdown, so nothing ever feels dead - this
    // fires before the click handler and before any network work starts.
    document.addEventListener('pointerdown', function (event) {
      var button = event.target.closest && event.target.closest('.product-listing__quickview-trigger');
      if (!button || button.classList.contains('gou-added')) return;
      button.classList.add('gou-pressed');
      setTimeout(function () { button.classList.remove('gou-pressed'); }, 220);
    }, { capture: true, passive: true });

    // Capture phase, so this runs before Forge's own handler on the button and
    // can take the click over for the products it can add directly.
    document.addEventListener('click', function (event) {
      var button = event.target.closest
        ? event.target.closest('.product-listing__quickview-trigger')
        : null;
      if (!button) return;

      if (button.hasAttribute('data-quick-add') && button.hasAttribute('data-variant-id')) {
        if (directAdd(button)) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return;
        }
      }

      // Everything else opens Forge's picker. Show a spinner until the drawer
      // is actually on screen, so the wait is visible instead of silent.
      if (button.getAttribute('data-gou-kind') === 'options') {
        button.classList.add('gou-busy');

        var waited = 0;
        var poll = setInterval(function () {
          waited += 100;
          var drawer = document.getElementById('slideout-quickview');
          var open = false;
          try {
            open = !!drawer && (drawer.matches(':popover-open') ||
                   drawer.classList.contains('is-open') ||
                   document.body.classList.contains('slideout-quickview--open'));
          } catch (e) {
            open = !!drawer && drawer.classList.contains('is-open');
          }
          if (open || waited >= 6000) {
            clearInterval(poll);
            button.classList.remove('gou-busy');
          }
        }, 100);
        return;
      }

      // Forge's own quick add path (kept for any button this script has not
      // classified yet): watch for the class it sets on success.
      if (!button.hasAttribute('data-quick-add')) return;

      var card = button.closest('.product-loop__item');
      var title = card ? (card.getAttribute('data-alpha') || '') : '';
      var w = 0;
      var p = setInterval(function () {
        w += 120;
        if (button.classList.contains('js-quick-adding')) {
          clearInterval(p);
          showAdded(button);
          toast(title ? title + ' added to bag' : 'Added to bag');
          return;
        }
        if (w >= 8000) clearInterval(p);
      }, 120);
    }, true);
  }

  /* ---- 3. Re-arm quick-add after an AJAX grid swap --------------------- */
  function rearm() {
    markOptionButtons();
    if (window.__gouObservePickers) window.__gouObservePickers();
    if (!window.WAU || !WAU.Quickshop || typeof WAU.Quickshop.init !== 'function') return;
    if (!document.querySelector('.js-quickview-trigger:not([data-gou-qs-seen])')) return;
    WAU.Quickshop.init();
  }

  var rearmTimer = null;
  function scheduleRearm() {
    clearTimeout(rearmTimer);
    rearmTimer = setTimeout(rearm, 200);
  }

  /* ---- Wiring ---------------------------------------------------------- */
  var settleTimer = null;

  function settle() {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(function () {
      if (injectBadges()) loadInitialCount();
      watchBadges();
      rearm();
    }, 180);
  }

  function start() {
    injectBadges();
    watchBadges();
    loadInitialCount();
    markOptionButtons();
    bindPrefetch();
    bindQuickAdd();
    scheduleRearm();

    // The wishlist script injects its own <li> into the same header list, and
    // collection filtering replaces the grid, so both the badge and the
    // quick-add buttons have to be re-checked when the DOM changes.
    if (document.body) {
      new MutationObserver(settle).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', function () {
    injectBadges();
    watchBadges();
    scheduleRearm();
  });
})();
